/* @jsxRuntime classic */
/** @jsx createElement */
import {
  BodyTwo,
  createElement,
  HeadlineThree,
  HeadlineTwo,
  OverlineOne,
  OverlineThree,
} from '@nucleus/ncu-section-builder';
import { Height, InfoButtonsWidth, InfoMaxWidth, SectionLayout, SectionTypeEnum } from '@nucleus/types/web';
import { Alignment } from '@nucleus/types/web/alignment';
import { Position } from '@nucleus/types/web';
import { SectionDefaultBuilder } from '../SectionDefaultBuilder';
import { ExtractPayload } from '@nucleus/types/web/section-data';
import {
  StandardBlockButtons,
  StandardSectionBackground,
  blockMediaOptionsDefault,
  buildCalendarItem,
  buildPlaceholderAction,
  buildPlaceholderMedia,
} from '../helpers';
import { defaultText } from '../text';

const builder = new SectionDefaultBuilder<SectionTypeEnum.Calendar>()
  .setWrapper((config, layoutPayload) => ({
    name: config.title,
    layout: SectionLayout.Full,
    height: Height.Medium,
    blockLayout: config.layout,
    blockMediaOptions: blockMediaOptionsDefault(config.lookConfig),
    ...layoutPayload,
  } as ExtractPayload<SectionTypeEnum.Calendar>))
  .addLayout('layout1', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-1',
    height: Height.Medium,
    blockLayoutVariant: 'variant2',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        actions: [buildPlaceholderAction()],
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        displayType: 'accordion',
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        items: [buildCalendarItem()],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        type: 'div',
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
      },
    ],
  }))
  .addLayout('layout2', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Small,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: false, headline: false, byline: false, body: false },
    blockInfoPosition: Position.Bottom,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Center,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        actions: [],
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        displayType: 'accordion',
        headline: <HeadlineTwo>{defaultText.headline.short}</HeadlineTwo>,
        items: [buildCalendarItem()],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
      },
    ],
  }))
  .addLayout('layout3', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-3',
    height: Height.Small,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: false, headline: false, byline: false, body: false },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Small,
    blockBodyAlignment: Alignment.Center,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        actions: [],
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        displayType: 'accordion',
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        items: [buildCalendarItem()],
        mediaItems: [
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
        ],
        type: 'div',
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
      },
    ],
  }))
  .addLayout('layout4', () => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Small,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Bottom,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockBodyMaxColumns: 2,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        actions: [],
        body: [
          ...BodyTwo({ children: defaultText.body.long }),
          ...BodyTwo({ children: '' }),
          ...BodyTwo({ children: defaultText.body.long2 }),
          ...BodyTwo({ children: '' }),
          ...BodyTwo({ children: defaultText.body.long3 }),
        ],
        displayType: 'accordion',
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        items: [buildCalendarItem()],
        mediaItems: [],
        type: 'div',
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
      },
    ],
  }))
  .addLayout('layout5', ({ lookConfig }) => ({
    backgroundMedia: { presetMediaKey: 'sectionBackground', presetMediaOrder: 'first' },
    blockBackgroundMedia: {
      useImage: true,
      image: lookConfig?.placeholders?.LightBokeh,
      presetMediaKey: 'blockBackground',
      presetMediaOrder: 'first',
    },
    colorPaletteVariation: 'variation-2',
    height: Height.Small,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Bottom,
    blockHeadlineAlignment: Alignment.Right,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        actions: [],
        body: <BodyTwo>{defaultText.body.short}</BodyTwo>,
        displayType: 'accordion',
        headline: <HeadlineThree>{defaultText.headline.long}</HeadlineThree>,
        items: [buildCalendarItem()],
        mediaItems: [],
        type: 'div',
        overline: <OverlineOne>{defaultText.overline.standard}</OverlineOne>,
      },
    ],
  }))
  .addLayout('layout6', () => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Medium,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Left,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Large,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blockButtonsWidth: InfoButtonsWidth.Stretch,
    blocks: [
      {
        displayType: 'accordion',
        items: [buildCalendarItem()],
      },
    ],
  }));

export default builder.build();
